import React, { useState } from "react";
import {
  Button,
  FormFeedback,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
} from "reactstrap";
import Flatpickr from "react-flatpickr";
import moment from "moment";
import { AsyncPaginatedSelect } from "components/common/AsyncPaginatedSelect";
import { getModeOfPaymentOptions } from "features/Meta/services/MetaService";
import { formatCurrency } from "utils/currency";
import type { EmployeeAdvance } from "features/EmployeeAdvances/types/employee-advance.types";

interface RefundModalProps {
  show?: boolean;
  record?: EmployeeAdvance | null;
  submitting?: boolean;
  onCloseClick?: () => void;
  onConfirm: (payload: {
    modeOfPaymentId: number;
    paymentDate?: string | null;
    referenceNumber?: string | null;
  }) => void;
}

const RefundModal = ({
  show,
  record,
  submitting = false,
  onCloseClick,
  onConfirm,
}: RefundModalProps) => {
  const [modeOfPaymentId, setModeOfPaymentId] = useState<number | "">("");
  const [paymentDate, setPaymentDate] = useState("");
  const [referenceNumber, setReferenceNumber] = useState("");
  const [attempted, setAttempted] = useState(false);

  const handleClose = () => {
    setModeOfPaymentId("");
    setPaymentDate("");
    setReferenceNumber("");
    setAttempted(false);
    onCloseClick?.();
  };

  const handleConfirm = () => {
    setAttempted(true);
    if (!modeOfPaymentId) return;
    onConfirm({
      modeOfPaymentId: Number(modeOfPaymentId),
      paymentDate: paymentDate || null,
      referenceNumber: referenceNumber.trim() || null,
    });
  };

  const remaining =
    record?.settleableAmount ??
    Math.max(0, (record?.amount ?? 0) - (record?.settledAmount ?? 0));

  return (
    <Modal fade isOpen={show} toggle={handleClose} centered>
      <ModalHeader toggle={handleClose}>
        Refund — {record?.code ?? `#${record?.id}`}
      </ModalHeader>
      <ModalBody>
        <p className="text-muted mb-3">
          This refunds the full remaining balance and creates the payment.
          Partial refunds aren't supported. The advance will move to{" "}
          <strong>Refunded</strong>.
        </p>
        <div className="alert alert-light border mb-3 py-2">
          <span className="text-muted me-2">Refund amount:</span>
          <span className="fw-semibold text-body">
            {formatCurrency(remaining)}
          </span>
        </div>
        <div className="mb-3">
          <Label className="form-label">
            Mode of payment <span className="text-danger">*</span>
          </Label>
          <AsyncPaginatedSelect
            fetchOptions={getModeOfPaymentOptions}
            placeholder="Search modes…"
            value={modeOfPaymentId}
            onChange={(val: any) => setModeOfPaymentId(val?.value ?? "")}
            isInvalid={attempted && !modeOfPaymentId}
          />
          {attempted && !modeOfPaymentId ? (
            <FormFeedback type="invalid" className="d-block">
              Mode of payment is required.
            </FormFeedback>
          ) : null}
        </div>
        <div className="mb-3">
          <Label className="form-label">
            Payment date <span className="text-muted fw-normal">(optional)</span>
          </Label>
          <Flatpickr
            className="form-control"
            placeholder="Select date"
            options={{ altInput: true, altFormat: "d M, Y", dateFormat: "d M, Y" }}
            onChange={(date: any) =>
              setPaymentDate(date[0] ? moment(date[0]).format("YYYY-MM-DD") : "")
            }
            value={paymentDate ? new Date(paymentDate) : ""}
            disabled={submitting}
          />
        </div>
        <div className="mb-0">
          <Label className="form-label">
            Reference number <span className="text-muted fw-normal">(optional)</span>
          </Label>
          <Input
            type="text"
            placeholder="UTR, cheque no., transaction id…"
            value={referenceNumber}
            onChange={(e) => setReferenceNumber(e.target.value)}
            disabled={submitting}
          />
        </div>
      </ModalBody>
      <ModalFooter>
        <Button color="light" disabled={submitting} onClick={handleClose}>
          Cancel
        </Button>
        <Button color="danger" disabled={submitting} onClick={handleConfirm}>
          {submitting ? "Please wait…" : "Refund"}
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default RefundModal;
